import { Button } from 'ui-components/components/button';
import { FlexRow } from 'ui-components/components/flex';
import { createColumnDefinitionBuilder } from 'ui-components/components/react-table-wrapper/create-column-definition-builder';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { useUtcFormattedDate } from '../../../../../../../hooks/use-datetime-formats';
import { messages } from './messages';
import styles from './shared-columns.styles.module.less';

type BaseProductRow = {
  productId: string;
  name: string;
  sku: string;
  thumbnailUrl?: string;
  location: string;
  lotId?: string;
  expirationDate?: string;
  isFirstProductRow: boolean;
};

type ColumnRow<T extends BaseProductRow = BaseProductRow> = {
  original: T;
};

type ProductCellProps<T extends BaseProductRow> = {
  row: ColumnRow<T>;
  openProductDetails: (productId: string) => void;
};

export const ProductCell = <T extends BaseProductRow>({ row, openProductDetails }: ProductCellProps<T>) => {
  const { thumbnailUrl, name, productId, sku, isFirstProductRow } = row.original;

  if (!isFirstProductRow) {
    return <div className={styles['product-cell-empty']} />;
  }

  return (
    <FlexRow className={styles['product-cell']}>
      {thumbnailUrl ? (
        <img alt={name} className={styles['image-placeholder']} src={thumbnailUrl} />
      ) : (
        <span className={styles['no-image-placeholder']} />
      )}
      <div className={styles['product-info']}>
        <Button className={styles['product-name']} onClick={() => openProductDetails(productId)} theme={THEME_CONSTANTS.link}>
          {name}
        </Button>
        <div className={styles['product-sku']}>
          <Text formattedMessageValues={{ sku }} textType={TextType.Caption} value={messages.productSku} />
        </div>
      </div>
    </FlexRow>
  );
};

export const ExpirationDateCell = <T extends BaseProductRow>({ row }: { row: ColumnRow<T> }) => {
  const { expirationDate } = row.original;
  const formattedDate = useUtcFormattedDate(expirationDate ?? '');

  if (!expirationDate) {
    return null;
  }

  return <Text textType={TextType.Paragraph} value={formattedDate} />;
};

export const LotIdCell = <T extends BaseProductRow>({ row }: { row: ColumnRow<T> }) => {
  return <Text textType={TextType.Paragraph} value={row.original.lotId ?? ''} />;
};

type OpenProductDetails = (productId: string) => void;

export const createProductColumnDefinition = <T extends BaseProductRow>(openProductDetails: OpenProductDetails) =>
  createColumnDefinitionBuilder({
    Header: <Text className={styles['product-column-header']} textType={TextType.Label} value={messages.product} />,
    accessor: 'product',
    Cell: ({ row }: { row: ColumnRow<T> }) => <ProductCell openProductDetails={openProductDetails} row={row} />,
    width: 250,
  })();

type CreateColumnOptions = {
  width?: number;
};

export const createLotIdColumnDefinition = <T extends BaseProductRow>({ width = 120 }: CreateColumnOptions = {}) =>
  createColumnDefinitionBuilder({
    Header: <Text textType={TextType.Label} value={messages.lotId} />,
    accessor: 'lotId',
    Cell: LotIdCell<T>,
    width,
  })();

export const createExpirationDateColumnDefinition = <T extends BaseProductRow>({ width = 120 }: CreateColumnOptions = {}) =>
  createColumnDefinitionBuilder({
    Header: <Text textType={TextType.Label} value={messages.expirationDate} />,
    accessor: 'expirationDate',
    Cell: ExpirationDateCell<T>,
    width,
  })();
